import React, {useEffect, useState} from "react";
import {Container, Row, Col, Image, Spinner, Alert, Card} from "react-bootstrap";
import {PortableText} from "@portabletext/react";
import {
  getUpcomingEvents,
  getPastEvents,
  getGaleries,
  urlFor,
} from "./lib/sanity";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const heure = d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} à ${heure}`;
}

function EventImages({images}) {
  const valid = (images || []).filter((img) => img && img.asset);
  if (valid.length === 0) return null;
  return (
    <Row className="mt-3 g-2">
      {valid.map((img) => (
        <Col xs={6} md={4} key={img._key}>
          <Image
            src={urlFor(img).width(600).auto("format").url()}
            alt={img.alt || img.caption || ""}
            fluid
            rounded
          />
          {img.caption && (
            <small className="text-muted d-block mt-1">{img.caption}</small>
          )}
        </Col>
      ))}
    </Row>
  );
}

function EventCard({event, past}) {
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body className="text-start">
        <Card.Title as="h3" className="h4">
          {event.titre}
        </Card.Title>
        <Card.Subtitle className="mb-3 text-muted">
          {past ? formatDate(event.date) : formatDateTime(event.date)}
          {event.lieu && ` — ${event.lieu}`}
        </Card.Subtitle>
        {event.description && (
          <div className="event-description">
            <PortableText value={event.description} />
          </div>
        )}
        {event.compteRenduUrl && (
          <p className="mt-3 mb-0">
            <a
              href={event.compteRenduUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm"
            >
              Télécharger le compte-rendu (PDF)
            </a>
          </p>
        )}
        <EventImages images={event.images} />
      </Card.Body>
    </Card>
  );
}

function GalerieCard({galerie}) {
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body className="text-start">
        <Card.Title as="h3" className="h4">
          {galerie.titre}
        </Card.Title>
        {galerie.date && (
          <Card.Subtitle className="mb-3 text-muted">
            {formatDate(galerie.date)}
          </Card.Subtitle>
        )}
        {galerie.description && <p>{galerie.description}</p>}
        <Row className="g-2">
          {(galerie.images || [])
            .filter((img) => img && img.asset)
            .map((img) => (
              <Col xs={6} md={4} key={img._key}>
                <Image
                  src={urlFor(img).width(600).auto("format").url()}
                  alt={img.alt || img.caption || galerie.titre}
                  fluid
                  rounded
                />
                {img.caption && (
                  <small className="text-muted d-block mt-1">
                    {img.caption}
                  </small>
                )}
              </Col>
            ))}
        </Row>
      </Card.Body>
    </Card>
  );
}

function Actualites() {
  const [upcoming, setUpcoming] = useState(null);
  const [past, setPast] = useState(null);
  const [galeries, setGaleries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Actualités — Temple de Sommières (ASTS)";
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getUpcomingEvents(), getPastEvents(), getGaleries()])
      .then(([up, pa, ga]) => {
        if (cancelled) return;
        setUpcoming(up);
        setPast(pa);
        setGaleries(ga);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Erreur de chargement");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = upcoming === null && past === null && galeries === null && !error;

  return (
    <Container as="main" id="main-content" className="actualites-page py-4">
      <h1 className="mb-4 text-start">Actualités</h1>

      {error && (
        <Alert variant="warning">
          Le contenu n'a pas pu être chargé pour le moment. Merci de réessayer
          plus tard.
        </Alert>
      )}

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement…</span>
          </Spinner>
        </div>
      )}

      {!loading && !error && (
        <>
          <section className="mb-5 text-start">
            <h2 className="border-bottom pb-2 mb-3">À venir</h2>
            {upcoming && upcoming.length > 0 ? (
              upcoming.map((ev) => <EventCard key={ev._id} event={ev} />)
            ) : (
              <p className="text-muted">Aucun événement programmé pour le moment.</p>
            )}
          </section>

          <section className="mb-5 text-start">
            <h2 className="border-bottom pb-2 mb-3">Comptes-rendus</h2>
            {past && past.length > 0 ? (
              past.map((ev) => <EventCard key={ev._id} event={ev} past />)
            ) : (
              <p className="text-muted">Aucun compte-rendu pour le moment.</p>
            )}
          </section>

          <section className="mb-5 text-start">
            <h2 className="border-bottom pb-2 mb-3">Galerie</h2>
            {galeries && galeries.length > 0 ? (
              galeries.map((g) => <GalerieCard key={g._id} galerie={g} />)
            ) : (
              <p className="text-muted">Aucune galerie pour le moment.</p>
            )}
          </section>
        </>
      )}
    </Container>
  );
}

export default Actualites;
